/**
 * useNotifications — subscribe to GET /api/notifications/stream (SSE).
 *
 * Wired to NotificationDropdown — UX audit 2026-05-26 §1 / cross-cutting #11.
 * The dropdown was already data-driven (it accepts `items`); this hook is the
 * missing producer. If the backend doesn't yet expose the stream (e.g. on a
 * staging deploy), the request returns 404, the connection is closed cleanly,
 * and the dropdown stays in its empty "all caught up" state. We re-try at most
 * `MAX_RECONNECT` times with exponential backoff so a flaky network doesn't
 * burn battery.
 *
 * Event schema (server side, when implemented):
 *   event: article_processed   data: {id:int, title:string}
 *   event: alignment_done      data: {merged:int, skipped:int}
 *   event: task_failed         data: {task:string, message:string}
 *   event: ping                data: {}                         // keepalive
 */
import { useEffect, useRef, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { apiUrl } from '../lib/apiBase';

const MAX_ITEMS = 30;
const MAX_RECONNECT = 5;

const HUMAN_TEXT = {
  article_processed: (p) => `Article processed: ${p.title || p.id || 'untitled'}`,
  alignment_done: (p) =>
    `Method alignment done — ${p.merged ?? 0} merged, ${p.skipped ?? 0} skipped`,
  task_failed: (p) => `Background task failed: ${p.task || 'unknown'} — ${p.message || ''}`,
};

function format(kind, payload) {
  const fn = HUMAN_TEXT[kind];
  if (fn) return fn(payload || {});
  return `${kind}: ${JSON.stringify(payload || {})}`;
}

export default function useNotifications({ enabled = true } = {}) {
  const [items, setItems] = useState([]);
  const reconnectsRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    abortRef.current = controller;

    const open = async () => {
      const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      try {
        await fetchEventSource(apiUrl('/api/notifications/stream'), {
          method: 'GET',
          headers,
          credentials: 'include',
          signal: controller.signal,
          openWhenHidden: false,
          onopen(response) {
            if (response.status === 404 || response.status === 501) {
              // Backend doesn't ship this stream yet — give up silently.
              const e = new Error('notifications stream unavailable');
              e.status = response.status;
              e.permanent = true;
              throw e;
            }
            if (!response.ok) {
              const e = new Error(`stream open ${response.status}`);
              e.status = response.status;
              throw e;
            }
            reconnectsRef.current = 0;
          },
          onmessage(msg) {
            if (!msg.event || msg.event === 'ping' || msg.event === 'ready') return;
            let payload = null;
            try {
              payload = msg.data ? JSON.parse(msg.data) : {};
            } catch (_) {
              return;
            }
            const text = format(msg.event, payload);
            const at = new Date().toISOString().replace('T', ' ').slice(0, 19);
            setItems((prev) => [{ id: msg.id || `${Date.now()}-${prev.length}`, text, at }, ...prev].slice(0, MAX_ITEMS));
          },
          onerror(err) {
            if (err && err.permanent) {
              throw err; // stop the lib's auto-retry; we won't reopen.
            }
            // Otherwise let fetch-event-source retry once; we cap externally.
            reconnectsRef.current += 1;
            if (reconnectsRef.current > MAX_RECONNECT) throw err;
          },
        });
      } catch (_) {
        // Swallow — empty inbox is a valid state per cross-cutting #11.
      }
    };

    open();
    return () => {
      controller.abort();
      abortRef.current = null;
    };
  }, [enabled]);

  return items;
}
